import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { acquireToken } from '@/auth/msalConfig';
import { getImpersonateAs, getImpersonateRole } from './sixBoxImpersonation';

export const BASE_URL = import.meta.env.VITE_API_BASE_URL as string || '';

const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120_000,
});

// ── Request interceptor — inject Bearer token ────────────────────────────────
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const token = await acquireToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (err: unknown) {
      // If token acquisition fails entirely, let the request proceed without
      // a token so the 401 interceptor below can handle re-login.
      console.warn('Could not acquire token for request:', err);
    }
    // Six-box impersonation. Scoped to six-box URLs so the headers never
    // leak to other domains. The server enforces the impersonator
    // allowlist, the CEO exclusion, the tier ceiling and read-only.
    // Arul 2026-08-21 — added the role dimension alongside Carolyn's
    // original person lens; either or both may be set.
    if (typeof config.url === 'string' && config.url.includes('/six-box/')) {
      const impersonateAs = getImpersonateAs();
      if (impersonateAs) {
        config.headers['x-atlas-impersonate-as'] = impersonateAs;
      }
      const impersonateRole = getImpersonateRole();
      if (impersonateRole) {
        config.headers['x-atlas-impersonate-role'] = impersonateRole;
      }
    }
    return config;
  },
  (error: unknown) => Promise.reject(error),
);

// ── Response interceptor — unwrap ApiResponse, map errors ───────────────────
apiClient.interceptors.response.use(
  (response) => {
    // Standard envelope is { success, data, message?, pagination? }. Two
    // unwrap modes depending on whether the response is paginated:
    //   - Paginated (pagination key present): keep the full envelope so
    //     callers can read `.data` + `.pagination`. Fixes the 2026-05-19
    //     P0 where the Tech Progression admin dashboard crashed on
    //     `list.pagination.totalPages` because pagination was being dropped.
    //   - Non-paginated: strip the envelope down to just `data` so callers
    //     can use the payload directly (preserves the existing contract for
    //     every getReview / getKPI / etc. caller).
    const responseData = response.data as Record<string, unknown> | null;
    if (
      responseData !== null &&
      typeof responseData === 'object' &&
      'success' in responseData &&
      'data' in responseData
    ) {
      if ('pagination' in responseData) {
        return response;
      }
      return { ...response, data: responseData['data'] };
    }
    return response;
  },
  async (error: AxiosError<{ message?: string; error?: { message?: string } | string }>) => {
    const status = error.response?.status;
    const errField = error.response?.data?.error;
    const serverMessage =
      error.response?.data?.message ??
      (typeof errField === 'object' ? errField?.message : errField) ??
      error.message;

    if (status === 401) {
      // Token is invalid or expired — force a full re-login
      console.warn('Received 401 — redirecting to login.');
      // Dispatch a custom event so the app can show the login page without
      // requiring a hard page refresh.
      window.dispatchEvent(new CustomEvent('atlas:unauthorized'));
      return Promise.reject(new Error('Your session has expired. Please sign in again.'));
    }

    if (status === 403) {
      // Arul 2026-09-14 — pass the server's reason through when it sent one.
      //
      // This used to hard-code the generic sentence and throw the server's
      // message away. Every distinct refusal — an impersonation tier
      // ceiling, a read-only lens, a locked submission, an out-of-scope
      // row — reached the user as the same eight words, which made a
      // permission problem impossible to diagnose from the screen. Carolyn
      // hit exactly this trying to view as Karen and could only report
      // that "it doesn't work". Note `serverMessage` falls back to axios's
      // own "Request failed with status code 403", which is no better than
      // the generic text, so only a message the API actually sent is used.
      const serverProvided =
        error.response?.data?.message ??
        (typeof errField === 'object' ? errField?.message : errField);
      return Promise.reject(
        new Error(serverProvided ?? 'You do not have permission to perform this action.'),
      );
    }

    if (status === 404) {
      return Promise.reject(new Error('The requested resource was not found.'));
    }

    if (status === 422) {
      return Promise.reject(new Error(serverMessage ?? 'Validation error. Please check your input.'));
    }

    if (status && status >= 500) {
      return Promise.reject(new Error('A server error occurred. Please try again later.'));
    }

    return Promise.reject(new Error(serverMessage ?? 'An unexpected error occurred.'));
  },
);

export default apiClient;
